/**
 * AI Disclosure & Automated Decision Rights
 *
 * Satisfies:
 *  - EU AI Act Art. 50: Transparency for limited-risk AI systems
 *  - nDSG Art. 21: Automated individual decision disclosure
 *  - GDPR Art. 22: Rights related to automated decision-making
 */

import { View, Text, Platform } from 'react-native';
import { Pressable } from 'react-native';
import { openExternalUrl } from '@/lib/linking';
import Head from 'expo-router/head';
import { useLocalSearchParams } from 'expo-router';
import {
  Sparkles,
  Brain,
  FileSearch,
  Lightbulb,
  Users,
  FileText,
  Target,
  ShieldCheck,
  Scale,
  UserCheck,
  Mail,
  AlertTriangle,
  ScanSearch,
} from 'lucide-react-native';
import Animated, { FadeInDown } from 'react-native-reanimated';
import { usePublicThemeColors } from '@/lib/useThemeColors';
import { useTrackScreen } from '@/lib/analytics';
import { PublicLayout } from '@/components/PublicLayout';
import { handleError } from '@/lib/errors';

interface SectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  colors: ReturnType<typeof usePublicThemeColors>;
  delay: number;
}

function Section({ title, icon, children, colors, delay }: SectionProps) {
  return (
    <Animated.View entering={FadeInDown.delay(delay).duration(500)} style={{ marginBottom: 28 }}>
      <View className="flex-row items-center gap-2.5 mb-3">
        {icon}
        <Text
          style={{ color: colors.text.primary }}
          className="text-base font-bold"
        >
          {title}
        </Text>
      </View>
      <View>{children}</View>
    </Animated.View>
  );
}

function Paragraph({ children, colors }: { children: React.ReactNode; colors: ReturnType<typeof usePublicThemeColors> }) {
  return (
    <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed mb-2">
      {children}
    </Text>
  );
}

function BulletItem({
  icon,
  title,
  description,
  colors,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  colors: ReturnType<typeof usePublicThemeColors>;
}) {
  return (
    <View className="flex-row items-start gap-3 mb-3">
      <View
        className="w-8 h-8 rounded-lg items-center justify-center mt-0.5"
        style={{ backgroundColor: colors.primary[50] }}
      >
        {icon}
      </View>
      <View className="flex-1">
        <Text style={{ color: colors.text.primary }} className="text-sm font-semibold mb-0.5">
          {title}
        </Text>
        <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed">
          {description}
        </Text>
      </View>
    </View>
  );
}

function RightItem({
  title,
  description,
  colors,
}: {
  title: string;
  description: string;
  colors: ReturnType<typeof usePublicThemeColors>;
}) {
  return (
    <View className="flex-row items-start gap-3 mb-3">
      <View
        className="w-6 h-6 rounded-full items-center justify-center mt-0.5"
        style={{ backgroundColor: colors.traffic.greenLight }}
      >
        <ShieldCheck size={13} color={colors.success} />
      </View>
      <View className="flex-1">
        <Text style={{ color: colors.text.primary }} className="text-sm font-semibold mb-0.5">
          {title}
        </Text>
        <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed">
          {description}
        </Text>
      </View>
    </View>
  );
}

export default function AiDisclosureScreen() {
  useTrackScreen('AiDisclosure');
  const colors = usePublicThemeColors();
  const { context } = useLocalSearchParams<{ context?: string }>();

  const handleRequestHumanReview = async () => {
    try {
      const subject = encodeURIComponent('Human Review Request — Automated Assessment');
      const body = encodeURIComponent(
        'I would like to request a human review of an automated assessment made by Inverio.\n\n' +
        'Account email: [your email]\n' +
        'Assessment type: [e.g., Investment Readiness Score]\n' +
        'Date of assessment: [approximate date]\n\n' +
        'Reason for review request:\n[Please describe your concern]\n'
      );
      await openExternalUrl(`mailto:support@inverio.ai?subject=${subject}&body=${body}`, 'AiDisclosure.email');
    } catch (err) {
      const { isCancelled } = handleError(err, { context: 'AiDisclosure.handleRequestHumanReview' });
      // Email client not available — silently report, not user-actionable on this screen
      if (!isCancelled) {
        // User will see the email address in the text below as fallback
      }
    }
  };

  return (
    <PublicLayout
      title="AI & Automated Decisions"
      titleIcon={<Sparkles size={22} color={colors.primary[600]} />}
    >
      {Platform.OS === 'web' && (
        <Head>
          <title>AI & Automated Decisions — Inverio</title>
          <meta name="description" content="How Inverio uses AI systems and your rights regarding automated decisions." />
          <link rel="canonical" href="https://inverio.ai/ai-disclosure" />
          <meta property="og:title" content="AI & Automated Decisions — Inverio" />
          <meta property="og:description" content="How Inverio uses AI systems and your rights regarding automated decisions." />
          <meta property="og:url" content="https://inverio.ai/ai-disclosure" />
        </Head>
      )}

      {/* Draft Banner */}
      <Animated.View
        entering={FadeInDown.delay(100).duration(500)}
        className="mb-6 p-4 rounded-2xl flex-row items-start gap-3"
        style={{
          backgroundColor: '#FEF3C7',
          borderWidth: 1,
          borderColor: '#F59E0B',
        }}
      >
        <AlertTriangle size={20} color="#D97706" style={{ marginTop: 2 }} />
        <Text style={{ color: '#92400E' }} className="text-sm font-medium flex-1 leading-relaxed">
          This document is a draft and should be reviewed by a qualified legal professional before publication.
        </Text>
      </Animated.View>

      {/* Last Updated */}
      <Animated.View entering={FadeInDown.delay(150).duration(500)} className="mb-6">
        <Text style={{ color: colors.text.tertiary }} className="text-sm">
          Last updated: March 16, 2026
        </Text>
      </Animated.View>

      {/* ── Section 1: How AI Powers Inverio ── */}
      <Section
        title="How AI Powers Inverio"
        icon={<Brain size={20} color={colors.primary[600]} />}
        colors={colors}
        delay={200}
      >
        <Paragraph colors={colors}>
          Inverio uses artificial intelligence to help investors evaluate startups and help founders
          understand their investment readiness. Below are the AI systems we currently operate:
        </Paragraph>

        <BulletItem
          icon={<Target size={16} color={colors.primary[600]} />}
          title="Investment Readiness Scoring"
          description="Evaluates startups across 15 investment dimensions using AI analysis of profile data and uploaded documents."
          colors={colors}
        />
        <BulletItem
          icon={<FileSearch size={16} color={colors.primary[600]} />}
          title="Due Diligence Assistant"
          description="RAG-powered document Q&A that helps investors extract insights from uploaded pitch decks and financials."
          colors={colors}
        />
        <BulletItem
          icon={<Lightbulb size={16} color={colors.primary[600]} />}
          title="Startup Insights"
          description="AI-powered recommendations and analysis to help founders improve their investment readiness."
          colors={colors}
        />
        <BulletItem
          icon={<Users size={16} color={colors.primary[600]} />}
          title="Team Analysis"
          description="Assesses founding team capabilities, experience, and composition as part of the scoring process."
          colors={colors}
        />
        <BulletItem
          icon={<FileText size={16} color={colors.primary[600]} />}
          title="Document Processing"
          description="Extracts structured data from uploaded documents (pitch decks, financials) for analysis."
          colors={colors}
        />
        <BulletItem
          icon={<ScanSearch size={16} color={colors.primary[600]} />}
          title="Strategic Analysis"
          description="Researches public web sources to produce competitive, market, industry-force, PESTEL and exit-strategy analyses. Every published statement links to a retained source, calculations are performed in application code rather than by the model, and an analysis that lacks supporting evidence is withheld rather than published. It runs only when a startup team member asks for it."
          colors={colors}
        />
      </Section>

      {/* ── Section 2: About Your Score ── */}
      <Section
        title="About Your Score"
        icon={<Scale size={20} color={colors.primary[600]} />}
        colors={colors}
        delay={250}
      >
        <Paragraph colors={colors}>
          This section explains how automated scoring works, as required under the Swiss Federal Act
          on Data Protection (nDSG Art. 21) and EU GDPR Art. 22.
        </Paragraph>

        <View
          className="rounded-xl p-4 mb-3"
          style={{ backgroundColor: colors.surface.secondary, borderWidth: 1, borderColor: colors.border.light }}
        >
          <Text style={{ color: colors.text.primary }} className="text-sm font-semibold mb-1">
            Purpose
          </Text>
          <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed">
            Evaluates your startup across 15 investment dimensions to help investors assess
            readiness. The score provides a structured, consistent framework for evaluation.
          </Text>
        </View>

        <View
          className="rounded-xl p-4 mb-3"
          style={{ backgroundColor: colors.surface.secondary, borderWidth: 1, borderColor: colors.border.light }}
        >
          <Text style={{ color: colors.text.primary }} className="text-sm font-semibold mb-1">
            Functionality
          </Text>
          <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed">
            AI evaluates sub-criteria against documented rubrics. Aggregation uses deterministic
            formulas (not a black box). The methodology is versioned and auditable.
          </Text>
        </View>

        <View
          className="rounded-xl p-4 mb-3"
          style={{ backgroundColor: colors.surface.secondary, borderWidth: 1, borderColor: colors.border.light }}
        >
          <Text style={{ color: colors.text.primary }} className="text-sm font-semibold mb-1">
            Data Sources
          </Text>
          <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed">
            Profile information you provide, uploaded documents (pitch decks, financials), and
            publicly available data about your company.
          </Text>
        </View>

        <View
          className="rounded-xl p-4 mb-3"
          style={{ backgroundColor: colors.surface.secondary, borderWidth: 1, borderColor: colors.border.light }}
        >
          <Text style={{ color: colors.text.primary }} className="text-sm font-semibold mb-1">
            Human Oversight
          </Text>
          <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed">
            Investors make all final decisions. The score is one input among many -- investors also
            conduct due diligence, meet founders, and evaluate independently.
          </Text>
        </View>

        <View
          className="rounded-xl p-4"
          style={{ backgroundColor: colors.amber?.light ?? '#FEF3C7', borderWidth: 1, borderColor: colors.border.light }}
        >
          <Text style={{ color: colors.text.primary }} className="text-sm font-semibold mb-1">
            Important
          </Text>
          <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed">
            This score is not a credit rating, financial advice, or binding assessment. It is an
            informational tool designed to support -- not replace -- human judgment.
          </Text>
        </View>
      </Section>

      {/* ── Section 3: Your Rights ── */}
      <Section
        title="Your Rights"
        icon={<ShieldCheck size={20} color={colors.success} />}
        colors={colors}
        delay={300}
      >
        <Paragraph colors={colors}>
          Under the GDPR (Art. 22) and the Swiss Federal Act on Data Protection (nDSG Art. 21), you
          have the following rights regarding automated decisions:
        </Paragraph>

        <RightItem
          title="Right to Explanation"
          description="You can request details about how your specific score was calculated, including which data points were most influential."
          colors={colors}
        />
        <RightItem
          title="Right to Human Review"
          description="You can request that a human review any automated assessment that affects you."
          colors={colors}
        />
        <RightItem
          title="Right to Contest"
          description="You can express your point of view and contest the outcome of any automated assessment."
          colors={colors}
        />
        <RightItem
          title="Right to Opt Out"
          description="You can withdraw consent for AI processing at any time. Contact us to exercise this right."
          colors={colors}
        />

        <View
          className="rounded-xl p-4 mt-1"
          style={{ backgroundColor: colors.surface.secondary, borderWidth: 1, borderColor: colors.border.light }}
        >
          <Text style={{ color: colors.text.primary }} className="text-sm font-semibold mb-1">
            How to Exercise Your Rights
          </Text>
          <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed">
            Contact us at support@inverio.ai or use the button below to request a human review. We
            will respond within 30 days of receipt.
          </Text>
        </View>
      </Section>

      {/* ── Section 4: Request Human Review ── */}
      <Animated.View entering={FadeInDown.delay(350).duration(500)} className="mb-8">
        <View
          className="rounded-2xl p-5"
          style={{
            backgroundColor: colors.surface.primary,
            borderWidth: 1,
            borderColor: colors.border.light,
          }}
        >
          <View className="flex-row items-center gap-2.5 mb-3">
            <UserCheck size={20} color={colors.primary[600]} />
            <Text style={{ color: colors.text.primary }} className="text-base font-bold">
              Request Human Review
            </Text>
          </View>
          <Paragraph colors={colors}>
            If you believe an automated assessment was inaccurate or you would like a human to review
            the decision, you can submit a request below. A member of our team will review your case
            and respond within 30 days.
          </Paragraph>
          <Pressable
            onPress={handleRequestHumanReview}
            className="flex-row items-center justify-center rounded-xl py-3.5 px-5 gap-2 mt-2"
            style={({ pressed }) => ({
              backgroundColor: pressed ? colors.primary[700] : colors.primary[600],
            })}
          >
            <Mail size={18} color="#FFFFFF" />
            <Text className="text-white font-semibold text-sm">
              Request Human Review
            </Text>
          </Pressable>
          <Text style={{ color: colors.text.tertiary }} className="text-xs text-center mt-3">
            This will open your email client with a pre-filled message to support@inverio.ai
          </Text>
        </View>
      </Animated.View>
    </PublicLayout>
  );
}
